import { memo, useCallback } from 'react';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';
import TableHead from '@mui/material/TableHead';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import TableHeadTypography from '../TableHeadTypography';
import useTypographyMediaQuery from '../../hooks/useTypographyMediaQuery';
import SortingArrow from '../SortingArrow';
import { sortingMethods } from 'src/constants/table';

const StyledTableHead = styled(TableHead, {
  shouldForwardProp: prop => prop !== 'hasCorner',
})(({ hasCorner }) => ({
  backgroundColor: '#3f3f3f',
  borderRadius: 0,
  ...(hasCorner
    ? {
        '.MuiTableRow-root th:first-child': {
          borderTopLeftRadius: '10px',
        },
        '.MuiTableRow-root th:last-child': {
          borderTopRightRadius: '10px',
        },
      }
    : {}),
}));

const StyledTableCell = styled(TableCell, {
  shouldForwardProp: prop => prop !== 'mediumScreen' && prop !== 'largeScreen',
})(() => ({
  padding: '10px 16px',
  // ...(mediumScreen || largeScreen
  //   ? {
  //       padding: '10px ',
  //     }
  //   : {}),
}));

const TableHeading = ({
  headings,
  onSortTable,
  onResetSortField,
  getListItemSortMethod,
  hasHeaderCorner = false,
  onCellSort,
  getCellSortMethod,
  hasActions = false,
  children,
  listCellStyles,
  listCellColSpan,
}) => {
  const { mediumScreen, largeScreen, smallScreen } = useTypographyMediaQuery();

  const onSort = useCallback(
    (sortMethod, name) => e => {
      e.preventDefault();

      onSortTable(sortMethod === sortingMethods.initial ? sortingMethods.asc : sortMethod, name);
    },
    [onSortTable],
  );

  return (
    <StyledTableHead hasCorner={hasHeaderCorner}>
      {children}
      <TableRow>
        {headings.map(({ Component, ...headingProps }, index) => {
          const sortMethod = !getListItemSortMethod
            ? sortingMethods.initial
            : getListItemSortMethod(headingProps?.name);
          // Most tables here are not sortable and pass no reset handler, so this stays undefined
          // for them rather than throwing on a double-click.
          const onHeadingDoubleClick = onResetSortField
            ? () => onResetSortField(headingProps.name)
            : undefined;

          return (
            <StyledTableCell
              key={`${headingProps.title}-${index}`}
              mediumScreen={mediumScreen}
              largeScreen={largeScreen}
              colSpan={listCellColSpan}
              sx={{
                top: '110px',
                ...(headingProps?.width ? { width: headingProps.width } : {}),
                ...(headingProps?.maxWidth ? { maxWidth: headingProps.maxWidth } : {}),
                ...listCellStyles,
              }}>
              <Stack gap={1} direction="row" alignItems="center">
                {!!Component ? (
                  <Component
                    title={headingProps.title}
                    name={headingProps.name}
                    onDoubleClick={onResetSortField}
                    onCellSort={onCellSort}
                    getCellSortMethod={getCellSortMethod}
                    filterFieldName={headingProps.filterFieldName}
                    sortingCellField={headingProps.sortingCellField}
                    sortFieldName={headingProps.sortFieldName}
                    field={headingProps.field}
                    hasSorting={headingProps?.hasSorting}
                  />
                ) : (
                  <>
                    {headingProps.tooltip ? (
                      <Tooltip
                        title={headingProps.tooltip}
                        arrow
                        componentsProps={{
                          tooltip: { sx: { bgcolor: '#3F3F3F', maxWidth: 320, border: '1px solid #666' } },
                        }}>
                        <Box component="span" display="inline-flex" sx={{ cursor: 'help' }}>
                          <TableHeadTypography
                            onDoubleClick={onHeadingDoubleClick}
                            mediumScreen={mediumScreen}
                            largeScreen={largeScreen}
                            smallScreen={smallScreen}
                            hasSorting={sortMethod !== sortingMethods.initial}>
                            {headingProps.title}
                          </TableHeadTypography>
                        </Box>
                      </Tooltip>
                    ) : (
                      <TableHeadTypography
                        onDoubleClick={onHeadingDoubleClick}
                        mediumScreen={mediumScreen}
                        largeScreen={largeScreen}
                        smallScreen={smallScreen}
                        hasSorting={sortMethod !== sortingMethods.initial}>
                        {headingProps.title}
                      </TableHeadTypography>
                    )}
                    {headingProps?.hasSorting && (
                      <SortingArrow
                        onClick={onSort(sortMethod, headingProps.name)}
                        sortMethod={sortMethod}
                        hasSorting={headingProps?.hasSorting}
                      />
                    )}
                  </>
                )}
              </Stack>
            </StyledTableCell>
          );
        })}
        {hasActions && (
          <StyledTableCell mediumScreen={mediumScreen} largeScreen={largeScreen}>
            <Stack direction="row" justifyContent="center">
              <Box width={16} height={16} />
            </Stack>
          </StyledTableCell>
        )}
      </TableRow>
    </StyledTableHead>
  );
};

export default memo(TableHeading);
